import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { incidentReportsManagementService, IncidentReport, ListIncidentReportsParams, UpdateIncidentReportRequest } from '../services/incidentReportsManagement';
import './IncidentReportsPage.css';
import { Table, TableColumn } from '../components/Table';

export const IncidentReportsPage: React.FC = () => {
  const [reports, setReports] = useState<IncidentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [viewingReport, setViewingReport] = useState<string | null>(null);
  const itemsPerPage = 50;

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListIncidentReportsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        incidentType: typeFilter !== 'all' ? typeFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await incidentReportsManagementService.listIncidentReports(params);
      setReports(response.reports);
      setTotalCount(response.count);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load incident reports');
      console.error('Error loading incident reports:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted
  }, [currentPage, statusFilter, typeFilter, searchTerm]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadReports();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Report ID', 'User ID', 'Incident Type', 'Status', 'Assigned To', 'Created', 'Updated'].join(','),
      ...reports.map(r => [
        r.reportId,
        r.userId,
        r.incidentType,
        r.status,
        r.assignedTo || '',
        r.createdAt,
        r.updatedAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incident-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'under_review': return '#007bff';
      case 'resolved': return '#28a745';
      case 'dismissed': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const columns: TableColumn<IncidentReport>[] = useMemo(() => [
    {
      header: 'Report ID',
      accessor: 'reportId',
      render: (value) => <div className="report-id">{(value as string).substring(0, 8)}...</div>,
    },
    {
      header: 'User ID',
      accessor: 'userId',
      render: (value) => <div className="secondary-text">{(value as string).substring(0, 8)}...</div>,
      hideOnTablet: true,
    },
    {
      header: 'Incident Type',
      accessor: 'incidentType',
      render: (value) => <span className="type-badge">{value as string}</span>,
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className="status-badge" style={{ backgroundColor: getStatusColor(value as string) }}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Assigned To',
      accessor: 'assignedTo',
      render: (value) => <div>{value ? (value as string).substring(0, 8) + '...' : 'Unassigned'}</div>,
      hideOnTablet: true,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
    },
    {
      header: 'Actions',
      accessor: 'reportId',
      render: (value) => (
        <button
          onClick={() => setViewingReport(value as string)}
          className="view-button"
        >
          View
        </button>
      ),
    },
  ], []);

  return (
    <div className="incident-reports-page">
      <div className="page-header">
        <h1>Incident Reports Management</h1>
        <div className="header-actions">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{totalCount}</span>
            </div>
          </div>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="reports-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        <div className="advanced-filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="harassment">Harassment</option>
            <option value="theft">Theft</option>
            <option value="violence">Violence</option>
            <option value="fraud">Fraud</option>
            <option value="unsafe_behavior">Unsafe Behavior</option>
            <option value="inappropriate_content">Inappropriate Content</option>
            <option value="other">Other</option>
          </select>

          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading incident reports...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={reports}
            getRowId={(row) => row.reportId}
            emptyMessage="No incident reports found"
            containerClassName="reports-table-container"
            className="reports-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || reports.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {viewingReport && (
        <ReportDetailModal
          reportId={viewingReport}
          onClose={() => {
            setViewingReport(null);
            loadReports();
          }}
        />
      )}
    </div>
  );
};

const ReportDetailModal: React.FC<{ reportId: string; onClose: () => void }> = ({ reportId, onClose }) => {
  const [report, setReport] = useState<IncidentReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on reportId change only
  }, [reportId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await incidentReportsManagementService.getIncidentReport(reportId);
      setReport(data);
      setStatus(data.status);
      setAssignedTo(data.assignedTo || '');
      setAdminNotes(data.adminNotes || '');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load report');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      const updates: UpdateIncidentReportRequest = {
        status: status as UpdateIncidentReportRequest['status'],
        assignedTo: assignedTo || undefined,
        adminNotes: adminNotes || undefined,
      };
      await incidentReportsManagementService.updateIncidentReport(reportId, updates);
      await loadReport();
      alert('Report updated successfully');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update report');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading report...</div>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Incident Report Details</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <label>Report ID:</label>
            <div>{report.reportId}</div>
          </div>
          <div className="detail-section">
            <label>User ID:</label>
            <div>{report.userId}</div>
          </div>
          <div className="detail-section">
            <label>Incident Type:</label>
            <div>{report.incidentType}</div>
          </div>
          <div className="detail-section">
            <label>Description:</label>
            <div className="description-text">{report.description}</div>
          </div>
          {report.bookingId && (
            <div className="detail-section">
              <label>Booking ID:</label>
              <div>{report.bookingId}</div>
            </div>
          )}
          {report.reportedUserId && (
            <div className="detail-section">
              <label>Reported User ID:</label>
              <div>{report.reportedUserId}</div>
            </div>
          )}
          <div className="detail-section">
            <label>Status:</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={updating}
              className="status-select"
            >
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
          <div className="detail-section">
            <label>Assigned To:</label>
            <input
              type="text"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="Admin user ID or email"
              className="input-field"
            />
          </div>
          <div className="detail-section">
            <label>Admin Notes:</label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add investigation notes..."
              rows={4}
              className="textarea-field"
            />
          </div>
          <div className="detail-section">
            <label>Created:</label>
            <div>{new Date(report.createdAt).toLocaleString()}</div>
          </div>
          <div className="detail-section">
            <label>Updated:</label>
            <div>{new Date(report.updatedAt).toLocaleString()}</div>
          </div>
          <div className="modal-actions">
            <button onClick={handleUpdate} disabled={updating} className="update-button">
              {updating ? 'Updating...' : 'Update Report'}
            </button>
            <button onClick={onClose} className="cancel-button">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};
